import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ServiceShowcase = () => {
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      id: 1,
      title: "Custom Software Development",
      subtitle: "Tailored Solutions for Complex Challenges",
      description: `Build enterprise-grade software solutions that scale with your business. Our custom development approach ensures every line of code serves your specific requirements and business objectives.

We specialize in creating robust, maintainable applications using modern architectures and proven development methodologies.`,
      icon: "Code2",
      color: "accent",
      features: [
        "Enterprise Architecture Design",
        "Microservices Development",
        "API Integration & Development",
        "Database Design & Optimization",
        "Cloud-Native Solutions",
        "DevOps & CI/CD Implementation"
      ],
      technologies: ["React", "Node.js", "Python", "AWS", "Docker", "Kubernetes"],
      demoImage: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&h=600&fit=crop",
      caseStudy: {
        client: "TechCorp Solutions",
        challenge: "Legacy system modernization",
        result: "40% performance improvement",
        timeline: "6 months"
      }
    },
    {
      id: 2,
      title: "Web Application Engineering",
      subtitle: "Modern Web Experiences That Convert",
      description: `Create high-performance web applications that deliver exceptional user experiences across all devices. From progressive web apps to complex enterprise portals, we build solutions that engage and convert.

Our web engineering expertise spans the full stack, ensuring seamless integration between frontend experiences and backend systems.`,
      icon: "Globe",
      color: "success",
      features: [
        "Progressive Web Applications",
        "Single Page Applications (SPA)",
        "Server-Side Rendering (SSR)",
        "Real-time Data Synchronization",
        "Advanced State Management",
        "Performance Optimization"
      ],
      technologies: ["React", "Next.js", "Vue.js", "TypeScript", "GraphQL", "MongoDB"],
      demoImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
      caseStudy: {
        client: "E-Commerce Plus",
        challenge: "Scalable online platform",
        result: "300% traffic increase handled",
        timeline: "4 months"
      }
    },
    {
      id: 3,
      title: "Mobile Solutions",
      subtitle: "Native & Cross-Platform Excellence",
      description: `Develop mobile applications that users love and businesses rely on. Whether native iOS/Android or cross-platform solutions, we create mobile experiences that drive engagement and business growth.

Our mobile development approach focuses on performance, user experience, and seamless integration with existing business systems.`,
      icon: "Smartphone",
      color: "warning",
      features: [
        "Native iOS & Android Development",
        "Cross-Platform Solutions",
        "Mobile UI/UX Design",
        "Offline-First Architecture",
        "Push Notifications & Analytics",
        "App Store Optimization"
      ],
      technologies: ["React Native", "Flutter", "Swift", "Kotlin", "Firebase", "Redux"],
      demoImage: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=600&fit=crop",
      caseStudy: {
        client: "HealthTech Innovations",
        challenge: "HIPAA-compliant mobile app",
        result: "50K+ active users",
        timeline: "8 months"
      }
    },
    {
      id: 4,
      title: "UI/UX Design",
      subtitle: "User-Centered Design That Converts",
      description: `Design digital experiences that users love and businesses profit from. Our design process combines user research, data-driven insights, and creative excellence to create interfaces that drive results.

We believe great design is invisible - it just works. Our designs focus on usability, accessibility, and conversion optimization.`,
      icon: "Palette",
      color: "purple-500",
      features: [
        "User Research & Testing",
        "Information Architecture",
        "Wireframing & Prototyping",
        "Visual Design Systems",
        "Accessibility Compliance",
        "Conversion Rate Optimization"
      ],
      technologies: ["Figma", "Adobe XD", "Sketch", "InVision", "Principle", "Framer"],
      demoImage: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&h=600&fit=crop",
      caseStudy: {
        client: "FinTech Startup",
        challenge: "Complex financial dashboard",
        result: "85% user satisfaction score",
        timeline: "3 months"
      }
    }
  ];

  const currentService = services?.[activeService];

  return (
    <section className="py-20 bg-gradient-to-b from-background to-primary/5">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Zap" size={16} />
            <span>Interactive Demonstrations</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Experience Our
            <span className="block bg-gradient-to-r from-accent to-success bg-clip-text text-transparent">
              Service Excellence
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Explore our comprehensive service offerings through interactive demonstrations 
            and real-world case studies that showcase our technical expertise.
          </p>
        </motion.div>

        {/* Service Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {services?.map((service, index) => (
            <button
              key={service?.id}
              onClick={() => setActiveService(index)}
              className={`flex items-center space-x-3 px-6 py-3 rounded-xl transition-all duration-300 ${
                index === activeService
                  ? `bg-${service?.color}/20 text-${service?.color} border border-${service?.color}/30 shadow-lg`
                  : 'bg-card text-muted-foreground hover:bg-muted/50 border border-border'
              }`}
            >
              <Icon name={service?.icon} size={20} />
              <span className="font-medium">{service?.title}</span>
            </button>
          ))}
        </motion.div>

        {/* Service Content */}
        <motion.div
          key={activeService}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-2 gap-12 items-center"
        >
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className={`inline-flex items-center space-x-2 bg-${currentService?.color}/10 text-${currentService?.color} px-4 py-2 rounded-full text-sm font-medium`}>
                <Icon name={currentService?.icon} size={16} />
                <span>{currentService?.subtitle}</span>
              </div>
              
              <h3 className="text-3xl font-bold text-foreground">
                {currentService?.title}
              </h3>
              
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                {currentService?.description}
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-foreground">Key Capabilities</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {currentService?.features?.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <div className={`w-2 h-2 bg-${currentService?.color} rounded-full`}></div>
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Technologies */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-foreground">Technologies We Use</h4>
              <div className="flex flex-wrap gap-2">
                {currentService?.technologies?.map((tech, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full border border-border"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                iconName="MessageCircle"
                iconPosition="left"
                className={`bg-${currentService?.color} text-white hover:bg-${currentService?.color}/90`}
              >
                Discuss Your Project
              </Button>
              <Button
                variant="outline"
                iconName="ExternalLink"
                iconPosition="right"
                className={`border-${currentService?.color}/30 text-${currentService?.color} `}
              >
                View Case Studies
              </Button>
            </div>
          </div>

          {/* Right Content - Demo & Case Study */}
          <div className="space-y-8">
            {/* Demo Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative group"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-3d">
                <Image
                  src={currentService?.demoImage}
                  alt={`${currentService?.title} demonstration`}
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">Live Demo Available</span>
                    <div className={`w-3 h-3 bg-${currentService?.color} rounded-full animate-pulse`}></div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Case Study Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className={`bg-gradient-to-br from-${currentService?.color}/10 to-${currentService?.color}/5 border border-${currentService?.color}/20 rounded-xl p-6 shadow-lg`}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-10 h-10 bg-${currentService?.color}/20 rounded-lg flex items-center justify-center`}>
                  <Icon name="TrendingUp" size={20} className={`text-${currentService?.color}`} />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">Success Story</h4>
                  <p className="text-sm text-muted-foreground">{currentService?.caseStudy?.client}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Challenge:</span>
                  <span className="text-sm font-medium text-foreground">{currentService?.caseStudy?.challenge}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Result:</span>
                  <span className={`text-sm font-medium text-${currentService?.color}`}>{currentService?.caseStudy?.result}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Timeline:</span>
                  <span className="text-sm font-medium text-foreground">{currentService?.caseStudy?.timeline}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceShowcase;